import { type PowerSyncDrizzleDB, user } from "@space/powersync";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";

/**
 * `UPDATE … WHERE id = ?` on `user` that **cannot get silently lost**.
 *
 * `user` was excluded from `patchRowVerified` because of a canon comment
 * (`listto-app`) that was never verified: "the row is created by the server on
 * signup, so it's already in the checkpoint before the first
 * preference". False here, and it remains so after the `ThemeProvider` seeding
 * guard (see below) — for two reasons that don't depend on who
 * seeds anything:
 *
 *   - `db` (PowerSync accessor) becomes non-null as soon as the local
 *     SQLite opens, not when the initial sync finishes.
 *   - `userId()` activates as soon as `signUpActor`/`signInActor` receive the
 *     server session (`packages/shared/src/auth/controllers.ts`) — a
 *     REST call independent of the PowerSync stream.
 *
 * Between `db`+`userId()` being ready and the initial checkpoint bringing the
 * server-created `user` row, there's a window in which a normal `UPDATE`
 * (changing language/theme/avatar) hits a row the local view
 * doesn't have yet: it matches ZERO rows and silently returns "success". That
 * window doesn't depend on anyone seeding a local row: it's the view
 * syncing, nothing more. Audit 2026-08-13 (same window first measured
 * in `habit-streak`/`boveda-vault`), details in
 * `../../../../infra/guides/POWERSYNC_VERIFIED_WRITE.md` §4.1.
 *
 * (Historical context, now CLOSED) Until the guard's commit there was a third
 * factor aggravating this same window for real accounts: the
 * `createEffect` of `ThemeProvider` (`apps/web/src/providers/
 * theme-provider.tsx`) locally inserted a 3-column "ghost" `user` row
 * for ANY `uid`, without distinguishing guest from real account,
 * racing against the real row the server had already created
 * (`packages/core-rust/src/db/user.rs:101,350`) and which was on its way via
 * sync. The `uid === VISITOR_ID` guard in `theme-provider.tsx` closed that specific
 * root cause — see the measurement in
 * `__tests__/theme-provider-seed-window.test.ts` and the anti-regression test in
 * `apps/web/src/providers/__tests__/theme-provider-seed-guard.test.tsx`.
 *
 * **This does NOT make `patchRowVerified`'s protection over
 * `user` unnecessary.** The two points above still open the same window for
 * any early write, with or without a ghost row involved — and the
 * startup guard also doesn't touch the cross-org/cohort isolation that
 * `scope` adds to `patchRowVerified` (`../powersync/verified-write.ts`,
 * closed the same day for ~20 of the ~58 calls, with the 6 Class 2
 * upserts of `academy/assessment/mutations.ts` documented separately because
 * they receive `organizationId` but don't go through `patchRowVerified`): the guard
 * decides WHETHER a row gets seeded at startup, not the `WHERE` used to read or
 * write any of them. If `patchRowVerified` is ever removed from `user`, let it be
 * because it's proven the window above no longer exists, not because
 * `ThemeProvider` stopped seeding ghosts.
 */
export async function updateUserLocaleMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  locale: string,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { locale }, options);
}

/**
 * Shared Drizzle mutation for updating User font family preference.
 */
export async function updateUserFontFamilyMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  fontFamilyId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { font_family: fontFamilyId }, options);
}

/**
 * Writes the avatar KEY into the local row.
 *
 * Written local-first (same as language, font, and theme) and not via REST: this way
 * the new photo shows instantly and survives an offline reload. The
 * BINARY does go via REST (`POST /api/avatar`) because it doesn't fit in the
 * sync queue; what travels through here is only the key that upload returns.
 *
 * `null` = "remove the photo" (initials come back).
 *
 * The guest has no syncable row of its own and cannot have an avatar (rule
 * zero), so there's no `VISITOR_ID` branch here like in `locale`/`theme`.
 */
export async function updateUserImageMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  imageKey: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { image: imageKey }, options);
}

/**
 * Shared Drizzle mutation for updating global theme.
 */
export async function updateGlobalThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  themeId: string | null,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { global_theme_id: themeId }, options);
}

/**
 * Shared Drizzle mutation for updating blur effect preference.
 */
export async function updateBlurMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  isBlur: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { is_blur: isBlur ? 1 : 0 }, options);
}

/**
 * Shared Drizzle mutation for updating follow device appearance preference.
 */
export async function updateFollowDeviceThemeMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  followDevice: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    user,
    user.id,
    userId,
    { follow_device_theme: followDevice ? 1 : 0 },
    options,
  );
}

/**
 * Consent to appear on leaderboards (privacy opt-in).
 *
 * Writes `user.leaderboard_opt_in` local-first; the server's write-path
 * reconciles the user's leaderboard entries (opt-out hides them from
 * others' stream; opt-in publishes them with their current XP). Server-authoritative:
 * the client never writes `leaderboard_entry` directly.
 */
export async function updateLeaderboardOptInMutation(
  db: PowerSyncDrizzleDB,
  userId: string,
  optIn: boolean,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(db, user, user.id, userId, { leaderboard_opt_in: optIn ? 1 : 0 }, options);
}
